<template>
	<AppModal
		modal-class="rounded-md min-w-[80vw]"
		:body-scroll="false"
		fill-screen
		@cancel="onCancel"
	>
		<AppDataGrid
			grid-name="grid"
			:resource-url="customResourceUrl || resource.uri"
			:resource-key="resource.name"
			:resource-name="resource.title"
			:grid-key="gridKey ?? `lookup:${resource.name}`"
			:use-default-preset="false"
			:filters="filters"
			:rsql-filters="rsqlFilters"
			:on-row-selected="onRowSelected"
			:row-class-rules="defineGridColors(resource.name)"
			:on-row-double-clicked="(e) => onConfirm(e.data.id)"
			:row-selection="selectionMode"
			@selected="(e) => (selectedRows = e.api.getSelectedRows())"
			@filtersChanged="onFiltersChanged"
		>
			<template #toolbar>
				<span/>
			</template>
		</AppDataGrid>
		<template #footer>
			<div
				class="absolute bottom-20 right-12 flex flex-row gap-2 bg-gray-700 rounded-xl shadow-xl p-3"
			>
				<slot name="actions" v-bind="{ selectedIds, selectedRows, onCancel }">
					<AppButton @click="onCancel">
						Annuleren
					</AppButton>
					<AppButton
						:disabled="selectedIds.length == 0"
						@click="onConfirm(undefined)"
					>
						<p class="flex">
							Selectie bevestigen ({{ selectedIds.length }}<span class="ml-0.5" v-if="showSelectedWeightSum">
              {{ selectedRows.length == 1 ? 'lot' : 'loten' }},
              {{ sum(selectedRows.map((row) => row.availableWeight)) }} kg,
              {{ sum(selectedRows.map((row) => row.availablePackageQuantity)) }} colli,
              {{ sum(selectedRows.map((row) => row.availableUnitQuantity)) }} eenheden
            </span>)
						</p>
					</AppButton>
				</slot>
			</div>
		</template>
	</AppModal>
</template>

<script setup lang="ts">
import { Resource } from "@q-food/q-food";
import { lotStatusColor } from "~utils/Grids/StatusColorRules.ts";
import { sum } from "@antfu/utils";
import { RowSelectedEvent } from "ag-grid-community";

function defineGridColors(resourceName: string) {
	switch (resourceName) {
		case "lot":
			return lotStatusColor();
		default:
			return null;
	}
}

const selectedRows = ref<any[]>([]);

const props = withDefaults(
	defineProps<{
		resource: Resource;
		/**
		 * Defaults to a `lookup:`-namespaced key rather than the resource's own. Left undefined it fell
		 * through to `AppDataGrid`'s `props.gridKey ?? props.resourceKey`, so the customer picker ran
		 * under `"relation"` — the very key the Relations index grid uses. It then opened on that
		 * grid's saved profile and column layout, and `persistColumnState` wrote every sort or resize
		 * made inside the modal straight back onto the user's index grid.
		 */
		gridKey?: string;
		selectionMode?: "singleRow" | "multiRow";
		customResourceUrl?: string;
		filters?: string;
		rsqlFilters?: string;
	}>(),
	{
		selectionMode: "singleRow",
	},
);

const showSelectedWeightSum = inject("gridLookup:showSelectedWeightSum");

const { customResourceUrl, resource } = toRefs(props);
const selectedIds = ref<string[]>([]);

function onRowSelected(e: RowSelectedEvent) {
	selectedIds.value = e.api.getSelectedRows().map((r) => r.id);
}

function onConfirm(value?: string) {
	if (typeof value === "string") {
		return emit("confirm", [value]);
	} else if (selectedIds.value.length > 0) {
		return emit("confirm", selectedIds.value);
	}
}

function onCancel() {
	selectedIds.value = [];
	emit("cancel");
}

const emit = defineEmits<{
	(e: "confirm", value: string[]): void;
	(e: "cancel"): void;
	(e: "filtersChanged");
}>();

const { escape, enter } = useMagicKeys();
whenever(escape, () => {
	onCancel();
});

const onFiltersChanged = (filterModel) => {
	emit("filtersChanged", filterModel);
};

whenever(enter, () => {
	onConfirm();
});
</script>

<style scoped></style>
